import React, { useState, useEffect, useRef } from 'react';

const BestoHrmsChatWidget = () => {
  // State management
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [showTyping, setShowTyping] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  const [showNotification, setShowNotification] = useState(true);

  // Refs
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const currentMessageRef = useRef(null);

  // Initialize component
  useEffect(() => {
    checkSystemHealth();
    
    // Hide notification after 5 seconds
    const timer = setTimeout(() => {
      setShowNotification(false);
    }, 5000);

    // Setup periodic health check
    const healthInterval = setInterval(checkSystemHealth, 30000);

    // Setup network status listeners
    const handleOnline = () => {
      setConnectionStatus('connected');
      checkSystemHealth();
    };
    const handleOffline = () => setConnectionStatus('offline');

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearTimeout(timer);
      clearInterval(healthInterval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, showTyping]);

  // System health check
  const checkSystemHealth = async () => {
    try {
      const response = await fetch('/chat/health');
      if (response.ok) {
        setConnectionStatus('connected');
      } else {
        throw new Error('Service unavailable');
      }
    } catch (error) {
      console.error('Health check failed:', error);
      setConnectionStatus('offline');
    }
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Toggle chat widget
  const toggleChat = () => {
    setChatOpen(!chatOpen);
    if (!chatOpen) {
      setShowNotification(false);
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 100);
    }
  };

  // Create new session
  const createSession = async () => {
    try {
      const response = await fetch('/chat/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: 'bestohrms_user' })
      });
      
      const data = await response.json();
      setCurrentSessionId(data.session_id);
      
    } catch (error) {
      console.error('Session creation error:', error);
      addMessage('Failed to create session. Please refresh and try again.', 'bot');
    }
  };

  // Add message to chat
  const addMessage = (content, sender, timestamp = null) => {
    const time = timestamp || new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    const newMessage = {
      id: Date.now() + Math.random(),
      content,
      sender,
      time,
      isComplete: true
    };
    
    setMessages(prev => [...prev, newMessage]);
    return newMessage;
  };

  // Create streaming message
  const createStreamingMessage = () => {
    const time = new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    const newMessage = {
      id: Date.now() + Math.random(),
      content: '',
      sender: 'bot',
      time,
      isComplete: false
    };
    
    setMessages(prev => [...prev, newMessage]);
    currentMessageRef.current = newMessage.id;
    return newMessage;
  };

  // Append to streaming message
  const appendToStreamingMessage = (content) => {
    setMessages(prev => prev.map(msg => 
      msg.id === currentMessageRef.current 
        ? { ...msg, content: msg.content + content }
        : msg
    ));
  };

  // Finalize streaming message
  const finalizeStreamingMessage = () => {
    setMessages(prev => prev.map(msg => 
      msg.id === currentMessageRef.current 
        ? { ...msg, isComplete: true }
        : msg
    ));
    currentMessageRef.current = null;
  };

  // Send quick message
  const sendQuickMessage = (message) => {
    setInputValue(message);
    setTimeout(() => sendMessage(message), 100);
  };

  // Send message
  const sendMessage = async (quickMessage = null) => {
    const message = quickMessage || inputValue.trim();
    if (!message || isStreaming) return;

    // Add user message
    addMessage(message, 'user');
    setInputValue('');

    // Show typing indicator
    setShowTyping(true);
    setIsStreaming(true);

    try {
      // Create session if needed
      if (!currentSessionId) {
        await createSession();
      }

      // Start streaming
      await startStreamingChat(message);
      
    } catch (error) {
      console.error('Chat error:', error);
      addMessage('Sorry, I encountered an error. Please try again.', 'bot');
      setShowTyping(false);
    }

    setIsStreaming(false);
  };

  // Start streaming chat
  const startStreamingChat = async (message) => {
    const url = `/chat/stream`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message,
          session_id: currentSessionId,
          user_id: 'bestohrms_user'
        })
      });

      if (!response.ok) {
        throw new Error(`Stream request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamingMessage = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'metadata') {
                setShowTyping(false);
                streamingMessage = createStreamingMessage();
                
              } else if (data.type === 'content') {
                if (streamingMessage) {
                  appendToStreamingMessage(data.data.content);
                }
                
              } else if (data.type === 'complete') {
                finalizeStreamingMessage();
                
              } else if (data.type === 'error') {
                setShowTyping(false);
                addMessage(data.data.fallback_response || 'Error occurred', 'bot');
                
              } else if (data.type === 'end') {
                break;
              }
              
            } catch (parseError) {
              console.error('Parse error:', parseError);
            }
          }
        }
      }
      
    } catch (error) {
      console.error('Streaming error:', error);
      setShowTyping(false);
      addMessage('Connection error. Please check your internet connection and try again.', 'bot');
    }
  };

  // Handle input key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle input change
  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  // Get current time for welcome message
  const getCurrentTime = () => {
    return new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
  };

  // Get connection status text
  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Online';
      case 'offline': return 'Offline';
      default: return 'Connecting...';
    }
  };

  // Get viewport dimensions
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive calculations
  const isMobile = windowSize.width <= 768;
  const isTablet = windowSize.width <= 1024 && windowSize.width > 768;
  const isSmallHeight = windowSize.height <= 600;

  const styles = {
    container: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      zIndex: 9999
    },
    
    chatIcon: {
      position: 'fixed',
      bottom: isMobile ? '16px' : '24px',
      right: isMobile ? '16px' : '24px',
      width: isMobile ? '56px' : '64px',
      height: isMobile ? '56px' : '64px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      border: 'none',
      outline: 'none',
      pointerEvents: 'auto',
      transform: chatOpen ? 'rotate(45deg)' : 'none',
      zIndex: 10001,
      ...(chatOpen && {
        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
      })
    },

    notificationBadge: {
      position: 'absolute',
      top: '-8px',
      right: '-8px',
      background: '#ff4757',
      color: 'white',
      borderRadius: '50%',
      width: isMobile ? '20px' : '24px',
      height: isMobile ? '20px' : '24px',
      display: showNotification ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: isMobile ? '10px' : '12px',
      fontWeight: 'bold',
      animation: 'pulse 2s infinite'
    },

    chatWidget: {
      position: 'fixed',
      bottom: isMobile ? '80px' : '100px',
      right: isMobile ? '8px' : (isTablet ? '16px' : '24px'),
      width: isMobile ? 'calc(100vw - 16px)' : (isTablet ? '380px' : '420px'),
      height: isMobile 
        ? `calc(100vh - 120px)` 
        : isSmallHeight 
          ? 'calc(100vh - 140px)' 
          : (isTablet ? '600px' : '650px'),
      maxWidth: isMobile ? 'none' : '420px',
      maxHeight: 'calc(100vh - 100px)',
      background: 'white',
      borderRadius: isMobile ? '12px' : '16px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
      transform: chatOpen ? 'translateY(0) scale(1)' : 'translateY(100%) scale(0.8)',
      opacity: chatOpen ? 1 : 0,
      visibility: chatOpen ? 'visible' : 'hidden',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      border: '1px solid #e1e8ed',
      pointerEvents: 'auto',
      zIndex: 10000
    },

    chatHeader: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: isMobile ? '16px' : '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: isMobile ? '12px 12px 0 0' : '16px 16px 0 0',
      minHeight: '60px'
    },

    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? '8px' : '12px',
      flex: 1,
      minWidth: 0
    },

    aiAvatar: {
      width: isMobile ? '36px' : '40px',
      height: isMobile ? '36px' : '40px',
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backdropFilter: 'blur(10px)',
      flexShrink: 0
    },

    headerInfo: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      flex: 1
    },

    headerTitle: {
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: '600',
      marginBottom: '2px',
      margin: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },

    status: {
      fontSize: isMobile ? '11px' : '12px',
      opacity: 0.9,
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },

    onlineIndicator: {
      width: isMobile ? '6px' : '8px',
      height: isMobile ? '6px' : '8px',
      background: connectionStatus === 'connected' ? '#2ecc71' : '#ff4757',
      borderRadius: '50%',
      animation: connectionStatus === 'connected' ? 'breathe 2s ease-in-out infinite' : 'none',
      flexShrink: 0
    },

    headerActions: {
      display: 'flex',
      gap: '8px',
      flexShrink: 0
    },

    headerBtn: {
      width: isMobile ? '28px' : '32px',
      height: isMobile ? '28px' : '32px',
      background: 'rgba(255, 255, 255, 0.2)',
      border: 'none',
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease'
    },

    sessionInfo: {
      background: 'rgba(255, 255, 255, 0.1)',
      padding: isMobile ? '6px 10px' : '8px 12px',
      margin: isMobile ? '6px 12px' : '8px 16px',
      borderRadius: '8px',
      fontSize: isMobile ? '10px' : '11px',
      opacity: 0.8,
      display: currentSessionId ? 'block' : 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },

    connectionStatus: {
      padding: isMobile ? '6px 10px' : '8px 12px',
      margin: isMobile ? '6px 12px' : '8px 16px',
      borderRadius: '8px',
      fontSize: isMobile ? '10px' : '11px',
      textAlign: 'center',
      background: connectionStatus === 'connected' 
        ? 'rgba(72, 187, 120, 0.1)' 
        : 'rgba(245, 101, 101, 0.1)',
      color: connectionStatus === 'connected' ? '#38a169' : '#e53e3e',
      border: connectionStatus === 'connected' 
        ? '1px solid rgba(72, 187, 120, 0.2)' 
        : '1px solid rgba(245, 101, 101, 0.2)',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },

    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: isMobile ? '12px' : '20px',
      background: '#f8fafc',
      backgroundImage: `
        radial-gradient(circle at 20% 50%, rgba(102, 126, 234, 0.05) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.05) 0%, transparent 50%)
      `,
      minHeight: 0
    },

    welcomeMessage: {
      textAlign: 'center',
      padding: isMobile ? '12px' : '20px',
      color: '#718096'
    },

    welcomeTitle: {
      color: '#2d3748',
      marginBottom: '8px',
      fontSize: isMobile ? '14px' : '16px',
      margin: '0 0 8px 0'
    },

    welcomeText: {
      fontSize: isMobile ? '12px' : '13px',
      lineHeight: '1.5',
      margin: 0
    },

    message: {
      marginBottom: isMobile ? '12px' : '16px',
      display: 'flex',
      alignItems: 'flex-end',
      animation: 'slideIn 0.3s ease-out'
    },

    messageUser: {
      justifyContent: 'flex-end'
    },

    messageContent: {
      maxWidth: isMobile ? '85%' : '75%',
      padding: isMobile ? '10px 14px' : '12px 16px',
      borderRadius: isMobile ? '12px' : '16px',
      position: 'relative',
      wordWrap: 'break-word',
      fontSize: isMobile ? '13px' : '14px',
      lineHeight: '1.4'
    },

    messageContentUser: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      borderBottomRightRadius: '4px'
    },

    messageContentBot: {
      background: 'white',
      color: '#2d3748',
      borderBottomLeftRadius: '4px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },

    messageTime: {
      fontSize: isMobile ? '9px' : '10px',
      opacity: 0.7,
      marginTop: '6px'
    },

    messageTimeUser: {
      textAlign: 'right'
    },

    messageTimeBot: {
      textAlign: 'left'
    },

    typingIndicator: {
      display: showTyping ? 'flex' : 'none',
      alignItems: 'center',
      gap: '8px',
      padding: isMobile ? '10px 14px' : '12px 16px',
      background: 'white',
      borderRadius: isMobile ? '12px' : '16px',
      borderBottomLeftRadius: '4px',
      maxWidth: '80px',
      margin: isMobile ? '0 12px 12px 12px' : '0 20px 16px 20px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },

    typingDot: {
      width: isMobile ? '5px' : '6px',
      height: isMobile ? '5px' : '6px',
      borderRadius: '50%',
      background: '#cbd5e0',
      animation: 'typing 1.5s ease-in-out infinite'
    },

    inputContainer: {
      padding: isMobile ? '12px 16px 16px' : '16px 20px 20px',
      background: 'white',
      borderTop: '1px solid #e2e8f0',
      flexShrink: 0
    },

    quickActions: {
      display: 'flex',
      gap: isMobile ? '6px' : '8px',
      marginBottom: isMobile ? '8px' : '12px',
      flexWrap: 'wrap'
    },

    quickAction: {
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: isMobile ? '12px' : '16px',
      padding: isMobile ? '4px 8px' : '6px 12px',
      fontSize: isMobile ? '11px' : '12px',
      color: '#4a5568',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      whiteSpace: 'nowrap'
    },

    inputWrapper: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: isMobile ? '8px' : '12px',
      background: '#f7fafc',
      borderRadius: isMobile ? '16px' : '20px',
      padding: isMobile ? '6px' : '8px',
      border: '1px solid #e2e8f0',
      transition: 'all 0.2s ease'
    },

    messageInput: {
      flex: 1,
      background: 'none',
      border: 'none',
      outline: 'none',
      color: '#2d3748',
      padding: isMobile ? '6px 10px' : '8px 12px',
      fontSize: isMobile ? '13px' : '14px',
      resize: 'none',
      maxHeight: isMobile ? '60px' : '80px',
      minHeight: '20px',
      fontFamily: 'inherit'
    },

    sendButton: {
      width: isMobile ? '32px' : '36px',
      height: isMobile ? '32px' : '36px',
      background: isStreaming 
        ? '#cbd5e0' 
        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      border: 'none',
      borderRadius: '50%',
      cursor: isStreaming ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
      flexShrink: 0
    },

    icon: {
      width: isMobile ? '18px' : '20px',
      height: isMobile ? '18px' : '20px',
      fill: 'white'
    },

    sendIcon: {
      width: isMobile ? '14px' : '16px',
      height: isMobile ? '14px' : '16px',
      fill: 'white'
    },

    minimizeIcon: {
      width: isMobile ? '14px' : '16px',
      height: isMobile ? '14px' : '16px',
      fill: 'white'
    }
  };

  // Remove the mobile responsive section since we've integrated it above
  return (
    <div style={styles.container} className="chat-widget-container">
      {/* Chat Icon */}
      <button
        style={styles.chatIcon}
        onClick={toggleChat}
        onMouseEnter={(e) => {
          if (!chatOpen) {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.4)';
          }
        }}
        onMouseLeave={(e) => {
          if (!chatOpen) {
            e.target.style.transform = 'none';
            e.target.style.boxShadow = '0 8px 32px rgba(102, 126, 234, 0.3)';
          }
        }}
      >
        <div style={styles.notificationBadge}>1</div>
        <svg style={styles.icon} viewBox="0 0 24 24">
          <path d="M20,2H4A2,2 0 0,0 2,4V22L6,18H20A2,2 0 0,0 22,16V4A2,2 0 0,0 20,2M6,9V7H18V9H6M14,11V13H6V11H14M16,15V17H6V15H16Z" />
        </svg>
      </button>

      {/* Chat Widget */}
      <div style={styles.chatWidget}>
        {/* Chat Header */}
        <div style={styles.chatHeader}>
          <div style={styles.headerLeft}>
            <div style={styles.aiAvatar}>
              <svg style={styles.icon} viewBox="0 0 24 24">
                <path d="M12,2A2,2 0 0,1 14,4C14,4.74 13.6,5.39 13,5.73V7H14A7,7 0 0,1 21,14H22A1,1 0 0,1 23,15V18A1,1 0 0,1 22,19H21V20A2,2 0 0,1 19,22H5A2,2 0 0,1 3,20V19H2A1,1 0 0,1 1,18V15A1,1 0 0,1 2,14H3A7,7 0 0,1 10,7H11V5.73C10.4,5.39 10,4.74 10,4A2,2 0 0,1 12,2M7.5,13A2.5,2.5 0 0,0 5,15.5A2.5,2.5 0 0,0 7.5,18A2.5,2.5 0 0,0 10,15.5A2.5,2.5 0 0,0 7.5,13M16.5,13A2.5,2.5 0 0,0 14,15.5A2.5,2.5 0 0,0 16.5,18A2.5,2.5 0 0,0 19,15.5A2.5,2.5 0 0,0 16.5,13Z" />
              </svg>
            </div>
            <div style={styles.headerInfo}>
              <h3 style={styles.headerTitle}>BestoHrms Assistant</h3>
              <div style={styles.status}>
                <div style={styles.onlineIndicator}></div>
                <span>{getConnectionStatusText()}</span>
              </div>
            </div>
          </div>
          <div style={styles.headerActions}>
            <button style={styles.headerBtn} onClick={toggleChat} title="Minimize">
              <svg style={styles.minimizeIcon} viewBox="0 0 24 24">
                <path d="M19,13H5V11H19V13Z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Session Info */}
        <div style={styles.sessionInfo}>
          BestoHrms Session: <span>{currentSessionId ? `${currentSessionId.slice(0, 8)}...` : 'No active session'}</span>
        </div>

        {/* Connection Status */}
        <div style={styles.connectionStatus}>
          {connectionStatus === 'connected' 
            ? '🟢 Connected to BestoHrms AI' 
            : connectionStatus === 'offline' 
              ? '🔴 No internet connection' 
              : '🟡 Connecting to BestoHrms AI'
          }
        </div>

        {/* Messages Container */}
        <div 
          style={styles.messagesContainer} 
          ref={messagesContainerRef}
          className="chat-messages-container"
        >
          {/* Welcome Message */}
          <div style={styles.welcomeMessage}>
            <h4 style={styles.welcomeTitle}>👋 Welcome to BestoHrms!</h4>
            <p style={styles.welcomeText}>
              I'm your BestoHrms AI Assistant. I can help you with HR queries, company policies, and general questions in both English and Marathi.
            </p>
          </div>
          
          {/* Initial Bot Message */}
          <div style={{...styles.message}}>
            <div style={{...styles.messageContent, ...styles.messageContentBot}}>
              <p style={{margin: 0, marginBottom: '8px'}}><strong>💡 How I can assist you:</strong></p>
              <p style={{margin: 0}}>• Get information about company benefits</p>
              <p style={{margin: 0}}>• General questions and guidance</p>
              <p style={{margin: 0}}>• Assistance in English or Marathi</p>
              <div style={{...styles.messageTime, ...styles.messageTimeBot}}>{getCurrentTime()}</div>
            </div>
          </div>

          {/* Dynamic Messages */}
          {messages.map((message) => (
            <div 
              key={message.id} 
              style={{
                ...styles.message,
                ...(message.sender === 'user' ? styles.messageUser : {})
              }}
            >
              <div style={{
                ...styles.messageContent,
                ...(message.sender === 'user' ? styles.messageContentUser : styles.messageContentBot)
              }}>
                <p style={{margin: 0}}>{message.content}</p>
                <div style={{
                  ...styles.messageTime,
                  ...(message.sender === 'user' ? styles.messageTimeUser : styles.messageTimeBot)
                }}>
                  {message.time}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Typing Indicator */}
        <div style={styles.typingIndicator}>
          <div style={{...styles.typingDot, animationDelay: '0s'}}></div>
          <div style={{...styles.typingDot, animationDelay: '0.2s'}}></div>
          <div style={{...styles.typingDot, animationDelay: '0.4s'}}></div>
        </div>

        {/* Input Container */}
        <div style={styles.inputContainer}>
          <div style={styles.quickActions}>
            <div 
              style={styles.quickAction} 
              onClick={() => sendQuickMessage('Tell me about benefits')}
              onMouseEnter={(e) => {
                e.target.style.background = '#667eea';
                e.target.style.color = 'white';
                e.target.style.borderColor = '#667eea';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = '#4a5568';
                e.target.style.borderColor = '#e2e8f0';
              }}
            >
              🎁 Benefits
            </div>
            <div 
              style={styles.quickAction} 
              onClick={() => sendQuickMessage('How can you help me?')}
              onMouseEnter={(e) => {
                e.target.style.background = '#667eea';
                e.target.style.color = 'white';
                e.target.style.borderColor = '#667eea';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
                e.target.style.color = '#4a5568';
                e.target.style.borderColor = '#e2e8f0';
              }}
            >
              ❓ About
            </div>
          </div>
                      <div style={styles.inputWrapper} className="chat-input">
            <textarea
              ref={messageInputRef}
              style={styles.messageInput}
              placeholder="Type your message to BestoHrms Assistant..."
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              rows={1}
            />
            <button 
              style={styles.sendButton} 
              onClick={() => sendMessage()}
              disabled={isStreaming}
              onMouseEnter={(e) => {
                if (!isStreaming) {
                  e.target.style.transform = 'scale(1.05)';
                  e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isStreaming) {
                  e.target.style.transform = 'none';
                  e.target.style.boxShadow = 'none';
                }
              }}
            >
              <svg style={styles.sendIcon} viewBox="0 0 24 24">
                <path d="M2,21L23,12L2,3V10L17,12L2,14V21Z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Required CSS animations and global styles */}
      <style jsx global>{`
        /* Ensure widget doesn't interfere with page scroll */
        body {
          overflow-x: hidden;
        }
        
        /* Scrollbar styling for webkit browsers */
        .chat-messages-container::-webkit-scrollbar {
          width: 4px;
        }
        
        .chat-messages-container::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .chat-messages-container::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 2px;
        }
        
        .chat-messages-container::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
        
        /* Animations */
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        
        @keyframes breathe {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes typing {
          0%, 60%, 100% { 
            transform: translateY(0); 
            background: #cbd5e0;
          }
          30% { 
            transform: translateY(-8px); 
            background: #667eea;
          }
        }
        
        /* Responsive breakpoints */
        @media (max-width: 480px) {
          .chat-widget-container {
            --chat-widget-width: calc(100vw - 12px);
            --chat-widget-height: calc(100vh - 100px);
            --chat-icon-size: 52px;
          }
        }
        
        @media (min-width: 481px) and (max-width: 768px) {
          .chat-widget-container {
            --chat-widget-width: calc(100vw - 16px);
            --chat-widget-height: calc(100vh - 120px);
            --chat-icon-size: 56px;
          }
        }
        
        @media (min-width: 769px) and (max-width: 1024px) {
          .chat-widget-container {
            --chat-widget-width: 380px;
            --chat-widget-height: 600px;
            --chat-icon-size: 60px;
          }
        }
        
        @media (min-width: 1025px) {
          .chat-widget-container {
            --chat-widget-width: 420px;
            --chat-widget-height: 650px;
            --chat-icon-size: 64px;
          }
        }
        
        /* Safe area handling for mobile devices */
        @supports (padding: max(0px)) {
          .chat-widget-mobile {
            padding-bottom: max(16px, env(safe-area-inset-bottom));
            padding-right: max(16px, env(safe-area-inset-right));
          }
        }
        
        /* Focus styles for accessibility */
        .chat-input:focus-within {
          border-color: #667eea !important;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
        }
        
        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .chat-widget {
            border: 2px solid #000;
          }
          
          .chat-message {
            border: 1px solid #000;
          }
        }
        
        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .chat-widget,
          .chat-icon,
          .chat-message {
            animation: none !important;
            transition: none !important;
          }
        }
        
        /* Print styles */
        @media print {
          .chat-widget-container {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BestoHrmsChatWidget;